import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link2, Trash2, Search } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { fmtTL } from "@/lib/utils";

export default function ItemMatching() {
  const [suggest, setSuggest] = useState([]);
  const [groups, setGroups] = useState([]);
  const load = () => {
    api.get("/item-groups/suggestions").then(r => setSuggest(r.data));
    api.get("/item-groups").then(r => setGroups(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async (name, unit, members) => {
    try {
      await api.post("/item-groups", { name, unit, members });
      toast.success(`${members.length} kalem "${name}" altında eşlendi`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Eşleme başarısız"); }
  };
  const remove = async (g) => {
    if (!window.confirm(`"${g.name}" eşlemesi kaldırılsın mı?`)) return;
    await api.delete(`/item-groups/${g.id}`); toast.success("Eşleme kaldırıldı"); load();
  };

  return (
    <div data-testid="item-matching-page">
      <PageHeader title="Benzer Kalem Eşleme" subtitle="Farklı yazılmış aynı iş kalemlerini eşleyin; fiyat uyarısı ve fiyat trendi eşlenen kalemleri birlikte değerlendirir." />
      <p className="text-sm text-muted-foreground mb-4 max-w-3xl">Büyük/küçük harf, noktalama ve boşluk farkları otomatik eşlenir. Aşağıdaki öneriler yazım benzerliğine göre üretilir; ölçü (5 CM / 6 CM) veya blok/ada harfi (A / B) farklı olan kalemler önerilmez. Her öneriyi kontrol edip onaylayın.</p>
      <Tabs defaultValue="suggest">
        <TabsList>
          <TabsTrigger value="suggest" data-testid="items-tab-suggest">Öneriler ({suggest.length})</TabsTrigger>
          <TabsTrigger value="manual" data-testid="items-tab-manual">Elle Eşle</TabsTrigger>
          <TabsTrigger value="groups" data-testid="items-tab-groups">Eşlemeler ({groups.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="suggest" className="grid lg:grid-cols-2 gap-4">
          {suggest.length === 0 && <p className="text-sm text-muted-foreground py-4">Öneri yok.</p>}
          {suggest.map(g => <SuggestCard key={g.unit + g.items.map(i => i.key).join("|")} group={g} onSave={save} />)}
        </TabsContent>
        <TabsContent value="manual"><ManualMatch onSave={save} /></TabsContent>
        <TabsContent value="groups" className="space-y-2">
          {groups.length === 0 && <p className="text-sm text-muted-foreground py-4">Henüz eşleme yok.</p>}
          {groups.map(g => (
            <div key={g.id} className="rounded-lg border border-border bg-card p-3 flex justify-between gap-3" data-testid={`item-group-${g.id}`}>
              <div>
                <div className="font-semibold text-sm">{g.name} <span className="text-xs text-muted-foreground font-normal">· {g.unit}</span></div>
                <div className="text-xs text-muted-foreground mt-1">{(g.labels?.length ? g.labels : g.members).join(" • ")}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(g)} data-testid={`delete-item-group-${g.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ItemRow({ it, checked, onCheck, disabled }) {
  return (
    <label className={`flex items-center gap-3 text-sm py-1.5 ${disabled ? "opacity-40" : "cursor-pointer"}`}>
      <Checkbox checked={checked} disabled={disabled} onCheckedChange={onCheck} />
      <span className="flex-1">{it.label}</span>
      <span className="font-mono-nums text-xs text-muted-foreground whitespace-nowrap">ort. {fmtTL(it.avg)} · {it.count} fiyat</span>
    </label>
  );
}

function SuggestCard({ group, onSave }) {
  const [sel, setSel] = useState(group.items.map(i => i.label));
  const [name, setName] = useState(group.items[0].label);
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="item-suggestion">
      <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">Birim: {group.unit}</div>
      {group.items.map(it => <ItemRow key={it.key} it={it} checked={sel.includes(it.label)}
        onCheck={v => setSel(v ? [...sel, it.label] : sel.filter(x => x !== it.label))} />)}
      <div className="flex gap-2 mt-2">
        <Input value={name} onChange={e => setName(e.target.value)} className="flex-1" />
        <Button size="sm" disabled={sel.length < 2} onClick={() => onSave(name, group.unit, sel)}
          className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="item-suggestion-save"><Link2 className="w-4 h-4 mr-1" /> Eşle</Button>
      </div>
    </div>
  );
}

function ManualMatch({ onSave }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const [name, setName] = useState("");
  const [tick, setTick] = useState(0);
  const unit = sel[0]?.unit;

  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return; }
    const t = setTimeout(() => api.get("/item-groups/items", { params: { q } }).then(r => setRows(r.data)), 300);
    return () => clearTimeout(t);
  }, [q, tick]);

  const toggle = (it, v) => {
    const next = v ? [...sel, it] : sel.filter(x => x.key !== it.key);
    setSel(next);
    if (v && !name) setName(it.label);
  };

  return (
    <div className="max-w-4xl">
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Kalem ara (örn: sıva, strafor, kazı)" value={q} onChange={e => setQ(e.target.value)} data-testid="manual-item-search" />
      </div>
      {sel.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 mb-3 text-sm">
          <div className="font-semibold mb-1">Seçilenler ({unit})</div>
          {sel.map(s => <div key={s.key} className="text-xs">• {s.label}</div>)}
          <div className="flex gap-2 mt-2">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ortak isim" className="flex-1" data-testid="manual-item-name" />
            <Button size="sm" disabled={sel.length < 2} onClick={async () => { await onSave(name, unit, sel.map(s => s.label)); setSel([]); setName(""); setTick(t => t + 1); }}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="manual-item-save">{sel.length} kalemi eşle</Button>
          </div>
        </div>
      )}
      <div className="rounded-lg border border-border bg-card px-3 divide-y divide-border">
        {rows.map(it => (
          <div key={it.key + it.unit} data-testid="manual-item-row">
            <ItemRow it={{ ...it, label: `${it.label} (${it.unit})${it.group_id ? " — eşlenmiş" : ""}` }} checked={sel.some(s => s.key === it.key && s.unit === it.unit)}
              disabled={!!unit && it.unit !== unit} onCheck={v => toggle(it, v)} />
          </div>
        ))}
        {q.trim().length >= 2 && rows.length === 0 && <p className="text-sm text-muted-foreground py-3">Sonuç yok.</p>}
      </div>
    </div>
  );
}
